/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import * as _ from 'lodash';
import $ from 'jquery';
import React, { Component, ReactComponentElement } from 'react';
import DialogContent from '@mui/material/DialogContent';
import { Dialog } from '@mui/material';
import TreeItem from '@mui/lab/TreeItem';
import TreeView from '@mui/lab/TreeView';

// first-party
import { BasicSearchAndSelectModel, MenuItemModel } from 'src/types/view-models/search-and-select.view.model';
import { CategoryModel } from 'src/types/category.model';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import { FauxMenuShape, FauxMenuSize } from 'src/types/view-models/faux-menu.view.model';
import BaseFauxMenu from '../faux-menu/BaseFauxMenu';
import { GlyphDown, GlyphRight, IconClose, IconPlusCircle, Input } from '@americanexpress/dls-react';
import IconLink from '../icon-link/IconLink';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import styles from './SearchAndSelect.module.scss';

export default class SearchAndSelect extends Component<BasicSearchAndSelectModel> {
    public override state: any = {
        selection: '',
        items: [] as MenuItemModel[],
        open: false,
        expanded: [] as MenuItemModel[],
        initialized: false
    };

    public override componentDidUpdate(): void {
        // APP-6689 when the dialog closes, set the selection to empty for next time
        if (!this.state.open && this.state.selection !== '') {
            this.handleSelect('');
        }
    }

    public static getDerivedStateFromProps(props: any, prevState: any): any {
        const propsItemsCount = _.sumBy(props.items, (item: MenuItemModel) =>
            item.children ? item.children.length : 0
        );
        const prevStateItemsCount = _.sumBy(prevState.items, (item: MenuItemModel) =>
            item.children ? item.children.length : 0
        );

        if (propsItemsCount === prevStateItemsCount && prevState.initialized) return prevState;

        return {
            selection: prevState.selection,
            items: props.items,
            expanded: props.expanded,
            open: prevState.open,
            initialized: true
        };
    }

    public override render(): ReactComponentElement<any> {
        return (
            <React.Fragment>
                <Dialog
                    className="searchAndSelectDialog"
                    id={this.props.id}
                    aria-labelledby="customized-dialog-title"
                    open={this.state.open}
                    maxWidth="lg"
                >
                    <DialogContent>
                        <ClickableIcon
                            className="close"
                            icon={<IconClose />}
                            onClick={() => this.setState({ open: false })}
                        />
                        <Typography variant={TypographyVariants.HEADING_S} className="page-title">
                            {this.props.title}
                        </Typography>
                        <Input
                            fullWidth
                            className="searchField"
                            value={this.state.selection}
                            variant="outlined"
                            onChange={(e: any) => this.handleSelect(e.target.value)}
                            placeholder="Type a category"
                        />
                        <div className="scroll">
                            <TreeView
                                aria-label="controlled"
                                defaultCollapseIcon={<GlyphDown />}
                                defaultExpandIcon={<GlyphRight />}
                                className="tree"
                                onNodeSelect={this.nodeSelect.bind(this)}
                                expanded={this.state.expanded}
                            >
                                {this.state.items.map((item: MenuItemModel) => {
                                    if (item.display) {
                                        return (
                                            <TreeItem
                                                key={item.value}
                                                nodeId={item.value}
                                                label={item.label}
                                                onClick={(e: React.SyntheticEvent) => {
                                                    this.onClick(e, item);
                                                }}
                                                className={
                                                    this.props.selected === item.value ? 'selected' : 'notselected'
                                                }
                                                id={'id-' + item.value}
                                            >
                                                {this.getChildren(item)}
                                                {this.getAddNew(item)}
                                            </TreeItem>
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                            </TreeView>
                        </div>
                    </DialogContent>
                </Dialog>
                <BaseFauxMenu
                    shape={FauxMenuShape.DEFAULT}
                    size={FauxMenuSize.DEFAULT}
                    fullWidth={this.props.fullWidth}
                    onClick={() =>
                        this.setState({
                            open: true,
                            selection: ''
                        })
                    }
                    className={this.getClasses()}
                    id={this.props.id}
                >
                    {this.props.name}
                </BaseFauxMenu>
            </React.Fragment>
        );
    }

    private getChildren(item: MenuItemModel): JSX.Element {
        return (
            <React.Fragment>
                {item.children
                    ? item.children.map((child: MenuItemModel, index: number) => {
                          if (child.display) {
                              return (
                                  <TreeItem
                                      key={child.value}
                                      nodeId={child.value}
                                      label={child.label}
                                      onClick={(e: React.SyntheticEvent) => {
                                          this.onClick(e, child);
                                      }}
                                      className={this.props.selected === child.value ? 'selected' : ''}
                                      id={'id-' + child.value}
                                  />
                              );
                          } else {
                              return null;
                          }
                      })
                    : null}
            </React.Fragment>
        );
    }

    private getAddNew(item: MenuItemModel): JSX.Element {
        return this.props.addName ? (
            <TreeItem
                key={'add'}
                nodeId={'-1'}
                label={
                    <IconLink
                        icon={<IconPlusCircle />}
                        text={this.props.addName}
                        typographyVariant={TypographyVariants.SUBTEXT}
                        noUnderline
                    />
                }
                onClick={() => {
                    if (this.props.addCallback) {
                        this.setState({ open: false });
                        this.props.addCallback(item);
                        if (this.props.value && this.props.setToValue) {
                            this.props.setToValue(this.props.value);
                        }
                    }
                }}
                id={'id-add'}
            />
        ) : null;
    }

    private onClick(e: React.SyntheticEvent, child: MenuItemModel): void {
        if ($(e.target).hasClass('MuiTreeItem-label')) {
            e.stopPropagation();
            e.preventDefault();
            void this.props.onClick(child);
            this.setState({ open: false });
        }
    }

    // a node got toggled, add it to the expanded array if it is not there or remove it if
    // it is
    private nodeSelect(event: React.SyntheticEvent, node: string): void {
        let expanded = this.state.expanded;

        if (expanded.includes(node)) {
            expanded = _.filter(expanded, (aNode: string) => node !== aNode);
        } else {
            expanded.push(node);
        }

        this.setState({
            expanded
        });
    }

    private handleSelect(value: string): void {
        const items = this.handleSearch(value, this.state.items);
        const expanded = _.filter(items, { expanded: true }).map((item: MenuItemModel) => item.value);

        this.setState({
            selection: value,
            items,
            expanded
        });
    }

    // get CSS classes
    private getClasses(): string {
        const classes: string[] = [];
        classes.push(styles.searchAndSelect);

        if (this.props.className) {
            classes.push(this.props.className);
        }

        if (this.props.fullWidth) {
            classes.push(styles.searchAndSelectFullWidth);
        } else {
            classes.push(styles.searchAndSelectDefaultWidth);
        }

        return classes.join(' ');
    }

    private handleSearch(searchTerm: string, items: MenuItemModel[]): MenuItemModel[] {
        searchTerm = searchTerm.toLowerCase();

        for (const item of items) {
            item.display = item.label.toLowerCase().includes(searchTerm);
            item.expanded = false;

            if (item.children) {
                let displayedChildren = false;
                for (const child of item.children) {
                    if (child.label.toLowerCase().includes(searchTerm)) {
                        child.display = true;
                        displayedChildren = true;
                    } else {
                        child.display = false;
                    }
                }

                if (displayedChildren) {
                    item.display = true;
                    item.expanded = true;
                }
            }
        }

        return items;
    }

    public static getMenuItemModels(categories: CategoryModel[]): MenuItemModel[] {
        return categories
            .filter((candidate: CategoryModel) => !candidate.parentId)
            .map((category: CategoryModel) => {
                const menuItem: MenuItemModel = this.createMenuItemModel(category);
                menuItem.children = categories
                    .filter((potentialChild: CategoryModel) => potentialChild.parentId === category.id)
                    .map((child: CategoryModel) => this.createMenuItemModel(child));
                if (!menuItem.children.length) menuItem.children = undefined;
                return menuItem;
            });
    }

    public static createMenuItemModel(category: CategoryModel): MenuItemModel {
        if (category?.name) {
            return {
                label: category.name,
                value: category.id,
                display: true
            };
        }
    }
}
